import componentRules from "./rules/generated/component-rules.json";
import layoutRules from "./rules/generated/layout-rules.json";
import pageRecipes from "./pages/page-recipes.json";
import mediaRules from "./rules/generated/media-rules.json";
import { IAIPageCreationContext, IAIPageDraft, IAIDraftComponent, IAIDraftSection, IAIPageVersionContext } from "./types";

type PropertyRule = {
  propertyName: string;
  displayLabel: string;
};

type ComponentRule = {
  name: string;
  displayName: string;
  editableProperties: PropertyRule[];
};

type RecipeSection = {
  name: string;
  purpose: string;
  layout: string;
  columns: number;
  components: string[];
};

const componentCatalog = componentRules.components as ComponentRule[];
const recipes = pageRecipes.pageKinds as Record<string, { label: string; sections: RecipeSection[] }>;
const layoutCatalog = layoutRules.layouts as Array<{ templateName: string; columnBased: boolean }>;
const uploadTargets = mediaRules.uploadTargets as Array<{ purpose: string; folderRecommendation: string }>;

const findComponent = (name: string): ComponentRule | undefined =>
  componentCatalog.find((component) => component.name === name);

const supportsProperty = (component: ComponentRule | undefined, propertyName: string): boolean =>
  !!component?.editableProperties.some((property) => property.propertyName === propertyName);

const titleFromUrl = (url: string, fallback: string): string => {
  if (!url || url === "/") {
    return fallback || "Homepage";
  }

  const slug = url.split("/").filter(Boolean).pop() ?? fallback;
  return slug
    .split(/[-_]/g)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const sentence = (...parts: Array<string | null | undefined>): string =>
  parts
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" ");

const applyProperty = (component: ComponentRule | undefined, propertyName: string, value: string) =>
  supportsProperty(component, propertyName) ? { propertyName, value } : null;

const buildComponent = (
  componentName: string,
  values: Array<{ propertyName: string; value: string | null | undefined }>,
  purpose: string
): IAIDraftComponent => {
  const component = findComponent(componentName);

  return {
    componentName,
    displayName: component?.displayName ?? componentName,
    purpose,
    propertyValues: values
      .map((value) => applyProperty(component, value.propertyName, value.value ?? ""))
      .filter((value): value is { propertyName: string; value: string } => value !== null)
  };
};

const buildHeroCopy = (context: IAIPageCreationContext | IAIPageVersionContext) => {
  const title = context.pageDetails.Title || titleFromUrl(context.pageDetails.Url, "New Page");
  const audience = context.brief.targetAudience || "your audience";
  const goal = context.brief.primaryGoal || "take the next step";
  const body = sentence(
    `${title} is designed for ${audience}.`,
    context.brief.contentInstructions,
    `Guide visitors toward ${goal}.`
  );

  return {
    title,
    body,
    cta: context.brief.primaryGoal || "Get started"
  };
};

const buildCardTopics = (context: IAIPageCreationContext | IAIPageVersionContext): string[] => {
  const requested = context.brief.keySections
    .split(/[,\n]/g)
    .map((item) => item.trim())
    .filter(Boolean);

  if (requested.length >= 3) {
    return requested.slice(0, 3);
  }

  return [
    "Flexible page layouts",
    "Structured content editing",
    "Publishing and site control"
  ];
};

const buildRecipeSections = (context: IAIPageCreationContext | IAIPageVersionContext): IAIDraftSection[] => {
  const recipe = recipes[context.brief.pageKind] ?? recipes["custom-page"];
  const hero = buildHeroCopy(context);
  const cardTopics = buildCardTopics(context);

  return recipe.sections.map((section, sectionIndex) => {
    const resolvedLayout = layoutCatalog.find((layout) => layout.templateName === section.layout)?.templateName ?? section.layout;
    const components = section.components.flatMap((componentName, componentIndex): IAIDraftComponent[] => {
      if (componentName === "Banner") {
        return [
          buildComponent("Banner", [
            { propertyName: "Text", value: `<h1>${hero.title}</h1><p>${hero.body}</p><p><strong>${hero.cta}</strong></p>` },
            { propertyName: "BackColour", value: "#171717" },
            { propertyName: "TextColour", value: "#f8f6f0" },
            { propertyName: "ImageAltText", value: `${hero.title} hero visual` }
          ], "Hero statement and opening CTA")
        ];
      }

      if (componentName === "TitleAndText") {
        const title = sectionIndex === 0
          ? hero.title
          : section.name;
        const body = sectionIndex === 0
          ? hero.body
          : sentence(context.brief.contentInstructions, `Keep the tone ${context.brief.tone || "clear and practical"}.`);

        return [
          buildComponent("TitleAndText", [
            { propertyName: "Title", value: title },
            { propertyName: "BodyText", value: `<p>${body}</p>` }
          ], section.purpose)
        ];
      }

      if (componentName === "BodyText") {
        return [
          buildComponent("BodyText", [
            { propertyName: "BodyText", value: `<p>${context.brief.contentInstructions}</p>` }
          ], section.purpose)
        ];
      }

      if (componentName === "Image") {
        return [
          buildComponent("Image", [
            { propertyName: "Caption", value: `${context.pageDetails.Title || "Page"} visual concept` },
            { propertyName: "AltText", value: `${context.pageDetails.Title || "Page"} supporting image` },
            { propertyName: "CaptionPosition", value: "bottom" }
          ], "Media placeholder tied to a generated/uploaded asset")
        ];
      }

      if (componentName === "Card") {
        const topic = cardTopics[componentIndex % cardTopics.length] ?? `Key point ${componentIndex + 1}`;
        return [
          buildComponent("Card", [
            { propertyName: "Title", value: topic },
            { propertyName: "BodyText", value: `<p>${sentence(topic, "Support this with concise proof, capability details, or a practical benefit for the user.")}</p>` },
            { propertyName: "Button1Text", value: context.brief.primaryGoal || "Learn more" },
            { propertyName: "Button2Text", value: "Contact us" }
          ], "Feature or benefit card")
        ];
      }

      if (componentName === "ContactProfile") {
        return [
          buildComponent("ContactProfile", [
            { propertyName: "ContactName", value: "SmoothBadger Team" },
            { propertyName: "Title", value: "Talk to us" },
            { propertyName: "InfoText", value: `<p>${sentence("Invite the visitor to get in touch.", context.brief.primaryGoal)}</p>` },
            { propertyName: "Email", value: "hello@example.com" }
          ], "Human contact CTA")
        ];
      }

      return [
        buildComponent(componentName, [], section.purpose)
      ];
    });

    return {
      name: section.name,
      template: resolvedLayout,
      columns: section.columns,
      purpose: section.purpose,
      components
    };
  });
};

export const buildPageDraft = (context: IAIPageCreationContext | IAIPageVersionContext): IAIPageDraft => {
  const sections = buildRecipeSections(context);
  const summaryPrompt = [
    `Create a ${context.brief.pageKind} for ${context.pageDetails.Title || titleFromUrl(context.pageDetails.Url, "this page")}.`,
    context.brief.targetAudience ? `Audience: ${context.brief.targetAudience}.` : "",
    context.brief.primaryGoal ? `Goal: ${context.brief.primaryGoal}.` : "",
    context.brief.tone ? `Tone: ${context.brief.tone}.` : "",
    context.brief.keySections ? `Required sections: ${context.brief.keySections}.` : "",
    context.brief.contentInstructions
  ].filter(Boolean).join(" ");

  return {
    pageId: context.pageId ?? null,
    versionId: "versionId" in context ? context.versionId : null,
    pageTitle: context.pageDetails.Title || titleFromUrl(context.pageDetails.Url, "Untitled page"),
    pageUrl: context.pageDetails.Url,
    pageKind: context.brief.pageKind,
    audience: context.brief.targetAudience,
    goal: context.brief.primaryGoal,
    tone: context.brief.tone,
    parentPageUrl: "parentPage" in context ? context.parentPage?.url ?? null : null,
    summaryPrompt,
    sections,
    mediaRequests: uploadTargets.map((target, index) => ({
      kind: index === 0 ? "hero-image" : "supporting-image",
      purpose: target.purpose,
      prompt: sentence(
        `Create a website-ready asset for ${context.pageDetails.Title || "this page"}.`,
        `Use it for ${target.purpose.toLowerCase()}.`,
        context.brief.targetAudience ? `Audience: ${context.brief.targetAudience}.` : "",
        context.brief.contentInstructions
      ),
      recommendedFolder: target.folderRecommendation,
      targetSectionIndex: null,
      targetComponentIndex: null,
      targetPropertyName: null
    }))
  };
};
